import { z } from 'zod';
import { DEFAULT_FACILITY_ROLE_PRESET, FacilityCapabilitySchema, FacilityRolePresetSchema, capabilitiesForPreset, } from './capability.schema.js';
/**
 * Phase 14b-2 — extracted from `facility/admin.schema.ts` to keep the
 * mega-file under Convention 06's 250-LOC soft cap.
 *
 * Resource: facility-user (the per-facility portal-user invite + remove
 * surface; not a separate persona type but a distinct resource within
 * the facility namespace). Sibling resource files:
 *   - `admin.schema.ts` — facility CRUD (list / detail / patch / lifecycle)
 *   - `user-admin.schema.ts` — THIS FILE (portal-user invite / remove)
 *
 * Per Convention 06's extraction-as-trigger pattern: when the
 * remaining file (`admin.schema.ts`) bundles further resources past
 * the cap (e.g., contract amend), peel them out the same way.
 */
/** Embedded in `AdminFacilityDetailResponseSchema.users` array.
 *  Imported by the facility/admin.schema.ts file for composition; not
 *  re-exported through the admin index because consumers access it
 *  through the detail-response type. */
export const AdminFacilityUserRowSchema = z.object({
    /** facility_user_account row id (NOT the user id). */
    id: z.string().uuid(),
    userId: z.string().uuid(),
    email: z.string().email(),
    displayName: z.string().nullable(),
    /** RBAC 3.2 — the granted capability set for this login at this facility. */
    capabilities: z.array(FacilityCapabilitySchema),
    invitedAt: z.string().datetime(),
    acceptedAt: z.string().datetime().nullable(),
});
/** POST /api/v1/admin/facilities/:id/users — operator invites a portal
 *  user. Creates the User (if no row exists for that email), creates the
 *  facility_user_account, creates a RoleAssignment with persona=
 *  FACILITY_USER (entityRef=facility.id), and sends a magic-link email
 *  pointing at the portal's /sign-in/verify route. */
export const AdminFacilityInviteUserRequestSchema = z
    .object({
    email: z.string().email().max(254).toLowerCase(),
    displayName: z.string().min(1).max(120).trim().optional(),
    /** RBAC 3.2 — the operator assigns the login's access two ways, and exactly
     *  one is expected:
     *   · `preset` — pick a named role (the v1 admin UI path); expanded to its
     *     capability set server-side.
     *   · `capabilities` — an explicit atom set (the forward path the v1.x raw
     *     checkbox editor will use; already accepted so the backend needs no
     *     change when that UI ships).
     *  Omit both → the DEFAULT preset (Coordinator), per the locked default. */
    preset: FacilityRolePresetSchema.optional(),
    // `.min(1)`: an explicit EMPTY set is a silent-lockout footgun (a login that
    // can do nothing but read its own profile), and `Boolean([]) === true` would
    // otherwise sneak past the preset-XOR-capabilities check below. A parked
    // login is expressed by removing the user, not by granting zero atoms.
    capabilities: z.array(FacilityCapabilitySchema).min(1).optional(),
})
    .refine((v) => !(v.preset && v.capabilities), {
    message: 'Provide either preset or capabilities, not both',
    path: ['capabilities'],
});
/** Resolve the invite request's access grant to a concrete atom set, applying
 *  the locked default when neither field is given. Single place so the invite
 *  controller and any future caller agree on precedence. */
export function resolveInviteCapabilities(req) {
    if (req.capabilities)
        return [...req.capabilities];
    return capabilitiesForPreset(req.preset ?? DEFAULT_FACILITY_ROLE_PRESET);
}
export const AdminFacilityInviteUserResponseSchema = z.object({
    ok: z.literal(true),
    userId: z.string().uuid(),
    facilityUserAccountId: z.string().uuid(),
    email: z.string().email(),
    /** True if a new User row was created (vs. an existing row reused). */
    newUser: z.boolean(),
});
/** PATCH /api/v1/admin/facilities/:id/users/:userId — RBAC 3.2: change an
 *  existing login's capability set. Same preset-XOR-capabilities shape as
 *  invite, but here BOTH-absent is an error (a no-op edit is a mistake, not a
 *  "use the default" — the default only makes sense at creation). */
export const AdminFacilityUpdateUserCapabilitiesRequestSchema = z
    .object({
    preset: FacilityRolePresetSchema.optional(),
    // `.min(1)` — same silent-lockout guard as invite (see above).
    capabilities: z.array(FacilityCapabilitySchema).min(1).optional(),
})
    .refine((v) => Boolean(v.preset) !== Boolean(v.capabilities), {
    message: 'Provide exactly one of preset or capabilities',
    path: ['capabilities'],
});
/** Resolve an edit request to a concrete atom set. No default here — the
 *  refine guarantees exactly one field is present. */
export function resolveUpdateCapabilities(req) {
    return req.capabilities ? [...req.capabilities] : capabilitiesForPreset(req.preset);
}
export const AdminFacilityUpdateUserCapabilitiesResponseSchema = z.object({
    ok: z.literal(true),
    userId: z.string().uuid(),
    capabilities: z.array(FacilityCapabilitySchema),
});
/** DELETE /api/v1/admin/facilities/:id/users/:userId — removes the
 *  portal-user association (does NOT delete the User row, which may
 *  belong to other facilities or personas). */
export const AdminFacilityRemoveUserResponseSchema = z.object({
    ok: z.literal(true),
});
//# sourceMappingURL=user-admin.schema.js.map